import { useCallback, useEffect } from 'react';
import {
  percentFormatter,
  PercentFormatterOptions,
  useFormattedInput,
} from 'react-formatted-input-hook';

import { useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';

import TextField, { TextFieldProps } from '../TextField/TextField.jsx';

export interface PercentFieldProps extends Omit<
  TextFieldProps,
  'value' | 'onChange'
> {
  value?: number | null;
  onChange?: (value: number | null, formattedValue: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  maxDecimals?: number;
  restFormatProps?: Omit<
    PercentFormatterOptions,
    'location' | 'value' | 'onChange'
  >;
}

/**
 * A component that renders a text field for percentage input.
 * @param value - The current value of the input.
 * @param onChange - A callback function that is called when the input value changes.
 * @param onFocus - The function to call when the number field gains focus.
 * @param onBlur - The function to call when the number field loses focus.
 * @param maxDecimals - The maximum number of decimal places allowed in the input.
 * @param restFormatProps - Additional options for formatting the input.
 * @param autoFocus - If `true`, the number field will be focused on mount.
 * @returns A PercentField component.
 */
const PercentField = ({
  value,
  onChange,
  onFocus,
  onBlur,
  maxDecimals = 2,
  restFormatProps = {},
  autoFocus,
  InputProps: restInputProps,
  ...restProps
}: PercentFieldProps) => {
  const { i18n } = useTranslation('@myn/mui');

  const { getProfileField } = useProfile();

  const locale =
    getProfileField('formattingConfig')?.numbersFormat || i18n.language;

  const formattedInput = useFormattedInput(
    percentFormatter({
      max: Infinity,
      maxDecimals,
      scale: 0,
      ...restFormatProps,
      locales: locale,
      value,
      onChange,
      liveUpdate: true,
    }),
  );

  const {
    ref,
    onFocus: libOnFocus,
    onBlur: libOnBlur,
    ...rest
  } = formattedInput.props;

  // A workaround for the issue with the `autoFocus` prop. You can read more about it here: https://github.com/nick-keller/react-formatted-input-hook/issues/3
  useEffect(() => {
    if (formattedInput.props.ref.current && autoFocus === true) {
      formattedInput.props.ref.current.focus();
    }
  }, [formattedInput.props.ref.current == null, autoFocus]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(event);
      }
      libOnFocus(event);
    },
    [onFocus, libOnFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      libOnBlur(event);
    },
    [onBlur, libOnBlur],
  );

  return (
    <TextField
      {...restProps}
      InputProps={{
        inputRef: ref,
        onFocus: handleFocus,
        onBlur: handleBlur,
        ...rest,
        ...restInputProps,
      }}
    />
  );
};

export default PercentField;
